import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { API_TAGS } from '@/shared/api';
import { type AttributeFilter, pickFilledAttributeFilters } from '@/entities/dashboards';
import {
  fetchCellDialogs,
  fetchConceptCard,
  fetchConcepts,
  fetchHierarchy,
  fetchMatrix,
  fetchStandardsMatrix,
  fetchTransitions,
} from './api';
import type {
  StrategistCellDialogsRequest,
  StrategistConceptCardRequest,
  StrategistConceptsRequest,
  StrategistHierarchyRequest,
  StrategistMatrixRequest,
  StrategistStandardsMatrixRequest,
  StrategistTransitionsRequest,
} from '../types';

/**
 * Тело запроса без незаполненных атрибутных условий.
 *
 * Только что добавленный атрибут — ещё пустое условие, и отправлять его нельзя: бэкенд
 * трактует, например, пустой `values: []` не как «условия нет», а как «ни одно значение не
 * подходит», и возвращает пустую выборку. Отсев идёт здесь, на границе запроса: тело входит
 * и в ключ кэша, поэтому набор пустого условия не даёт лишнего рефетча.
 */
const withFilledFilters = <T extends { filters?: AttributeFilter[] }>(body: T): T =>
  body.filters === undefined ? body : { ...body, filters: pickFilledAttributeFilters(body.filters) };

/**
 * Ключи кэша react-query раздела «Стратег».
 * Тело запроса целиком входит в ключ — смена фильтров/периода/цели триггерит рефетч.
 */
export const strategistKeys = {
  all: [API_TAGS.STRATEGIST] as const,
  cellDialogs: (body: StrategistCellDialogsRequest) => [...strategistKeys.all, 'cell-dialogs', body] as const,
  concept: (body: StrategistConceptCardRequest) => [...strategistKeys.all, 'concept', body] as const,
  concepts: (body: StrategistConceptsRequest) => [...strategistKeys.all, 'concepts', body] as const,
  hierarchy: (body: StrategistHierarchyRequest) => [...strategistKeys.all, 'hierarchy', body] as const,
  matrix: (body: StrategistMatrixRequest) => [...strategistKeys.all, 'matrix', body] as const,
  standardsMatrix: (body: StrategistStandardsMatrixRequest) =>
    [...strategistKeys.all, 'standards-matrix', body] as const,
  transitions: (body: StrategistTransitionsRequest) => [...strategistKeys.all, 'transitions', body] as const,
};

/**
 * Хук списка концептов с агрегатами по выборке (POST /strategist-data/concepts).
 */
export const useStrategistConcepts = (body: StrategistConceptsRequest, enabled = true) => {
  const request = withFilledFilters(body);
  return useQuery({
    queryKey: strategistKeys.concepts(request),
    queryFn: () => fetchConcepts(request),
    placeholderData: keepPreviousData,
    enabled,
  });
};

/**
 * Хук диалогов за срезом (POST /strategist-data/cell-dialogs): влияние среза и
 * список диалогов. Запрос выполняется только когда задан хотя бы один адрес
 * среза — `objectionId`, `argumentId`, `conceptId` или `block`.
 */
export const useStrategistCellDialogs = (body: StrategistCellDialogsRequest) => {
  const request = withFilledFilters(body);
  return useQuery({
    queryKey: strategistKeys.cellDialogs(request),
    queryFn: () => fetchCellDialogs(request),
    enabled: Boolean(body.objectionId || body.argumentId || body.conceptId || body.block),
  });
};

/**
 * Хук карточки понятия (POST /strategist-data/concept): агрегаты и реальные
 * реплики понятия по выборке. Запрос выполняется только когда задан `conceptId`.
 */
export const useStrategistConceptCard = (body: StrategistConceptCardRequest) => {
  const request = withFilledFilters(body);
  return useQuery({
    queryKey: strategistKeys.concept(request),
    queryFn: () => fetchConceptCard(request),
    enabled: Boolean(body.conceptId),
  });
};

/**
 * Хук кластеров понятий (иерархия) выбранной категории на пороге близости
 * (POST /strategist-data/hierarchy).
 */
export const useStrategistHierarchy = (body: StrategistHierarchyRequest, enabled = true) => {
  const request = withFilledFilters(body);
  return useQuery({
    queryKey: strategistKeys.hierarchy(request),
    queryFn: () => fetchHierarchy(request),
    placeholderData: keepPreviousData,
    enabled,
  });
};

/**
 * Хук матрицы «возражение × аргумент» (POST /strategist-data/matrix).
 */
export const useStrategistMatrix = (body: StrategistMatrixRequest, enabled = true) => {
  const request = withFilledFilters(body);
  return useQuery({
    queryKey: strategistKeys.matrix(request),
    queryFn: () => fetchMatrix(request),
    placeholderData: keepPreviousData,
    enabled,
  });
};

/**
 * Хук матрицы «понятие × блок стандарта» (POST /strategist-data/standards-matrix).
 */
export const useStrategistStandardsMatrix = (body: StrategistStandardsMatrixRequest, enabled = true) => {
  const request = withFilledFilters(body);
  return useQuery({
    queryKey: strategistKeys.standardsMatrix(request),
    queryFn: () => fetchStandardsMatrix(request),
    placeholderData: keepPreviousData,
    enabled,
  });
};

/**
 * Хук переходов «возражение → следующий аргумент» (POST /strategist-data/transitions).
 */
export const useStrategistTransitions = (body: StrategistTransitionsRequest, enabled = true) => {
  const request = withFilledFilters(body);
  return useQuery({
    queryKey: strategistKeys.transitions(request),
    queryFn: () => fetchTransitions(request),
    placeholderData: keepPreviousData,
    enabled,
  });
};
